/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Translation Export DTOs & OpenAPI Schemas (Task 8.5.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from 'zod';
import { ReassemblyStatsSchema } from './reassembly.dto';

/**
 * Supported industrial export formats for translation projects.
 */
export const ExportFormatSchema = z
  .enum(['txt', 'md', 'html', 'docx', 'xliff', 'tmx'])
  .describe('Target file export format');

export type ExportFormat = z.infer<typeof ExportFormatSchema>;

/**
 * Request payload schema for exporting a translation project.
 */
export const ExportTranslationRequestSchema = z
  .object({
    format: ExportFormatSchema.default('txt').describe('Target export format'),
    targetLang: z
      .string()
      .min(2)
      .max(10)
      .optional()
      .describe('Target language code (e.g. "vi", "fr"). Defaults to project target_lang'),
    fallbackToSource: z
      .boolean()
      .optional()
      .default(true)
      .describe('Whether to retain source text if target translation is missing'),
    onlyReviewed: z
      .boolean()
      .optional()
      .default(false)
      .describe('If true, only reviewed/approved segments are included in the export'),
    includeNotes: z
      .boolean()
      .optional()
      .default(true)
      .describe('Whether to include metadata notes (QA warnings, TM similarity) in XLIFF / HTML exports'),
    xliffVersion: z
      .enum(['1.2', '2.0'])
      .optional()
      .default('1.2')
      .describe('Specification version for XLIFF format (1.2 or 2.0)'),
    delivery: z
      .enum(['download', 'json'])
      .optional()
      .default('download')
      .describe(
        'Delivery mechanism: "download" sends file attachment with Content-Disposition, "json" returns metadata and URLs',
      ),
    saveToR2: z
      .boolean()
      .optional()
      .default(true)
      .describe('Whether to persist the generated file in Cloudflare R2 bucket for zero-egress CDN delivery'),
  })
  .openapi('ExportTranslationRequest');

/**
 * Response payload schema for JSON delivery mode of translation export.
 */
export const ExportTranslationResponseSchema = z
  .object({
    translationId: z.string().describe('Translation project ID'),
    format: ExportFormatSchema.describe('Export format produced'),
    filename: z.string().describe('Suggested download file name with extension'),
    mimeType: z.string().describe('MIME content type of the exported file'),
    fileSizeBytes: z.number().int().nonnegative().describe('File size in bytes'),
    r2Key: z.string().optional().describe('Storage key in Cloudflare R2 bucket if saved'),
    downloadUrl: z.string().optional().describe('Direct CDN download URL with zero egress fees'),
    content: z
      .string()
      .optional()
      .describe('Textual content of the export if format is text-based (txt, md, html, xliff, tmx)'),
    stats: ReassemblyStatsSchema.optional().describe('Reassembly statistics'),
  })
  .openapi('ExportTranslationResponse');

export type ExportTranslationRequestDto = z.infer<typeof ExportTranslationRequestSchema>;
export type ExportTranslationResponseDto = z.infer<typeof ExportTranslationResponseSchema>;
